import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Weights, OpportunityScore } from '@/types/opportunity';
import { useNavigate } from 'react-router-dom';
import { storePendingAssessment } from '@/utils/assessmentStorage';

export const useSaveAssessment = (weights: Weights, scoredOpportunities: OpportunityScore[]) => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    console.log('Starting save assessment...', { weights, scoredOpportunities });
    setIsSaving(true);
    try {
      const {
        data: { user },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError || !user) {
        console.log('Auth error or no user:', authError);
        toast.error('Please sign in to save your assessment');
        storePendingAssessment({
          weights,
          scoredOpportunities,
          returnTo: '/assessment'
        });
        navigate('/login');
        return;
      }

      console.log('Authenticated user:', user.id);

      // Get the most recent assessment for this user
      const { data: existingAssessments, error: checkError } = await supabase
        .from('assessments')
        .select('id, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (checkError) {
        console.error('Error checking existing assessment:', checkError);
        throw checkError;
      }

      console.log('Existing assessments check:', existingAssessments);

      let result;
      
      if (existingAssessments && existingAssessments.length > 0) {
        const mostRecentAssessment = existingAssessments[0];
        console.log('Updating most recent assessment:', mostRecentAssessment.id);
        // Update existing assessment
        result = await supabase
          .from('assessments')
          .update({
            weights,
            scored_opportunities: scoredOpportunities,
          })
          .eq('id', mostRecentAssessment.id)
          .select();
      } else {
        console.log('Creating new assessment for user:', user.id);
        // Insert new assessment
        result = await supabase
          .from('assessments')
          .insert({
            user_id: user.id,
            weights,
            scored_opportunities: scoredOpportunities,
          })
          .select();
      }

      const { error } = result;

      if (error) {
        console.error('Error saving assessment:', error);
        throw error;
      }

      console.log('Assessment saved successfully');
      return result;
    } catch (error) {
      console.error('Error in handleSave:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  return { handleSave, isSaving };
};